// src/pages/Bazzar.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';
import { styled } from '@mui/material/styles';
import {
    Button,
    TextField,
    Typography,
    Grid,
    Box,
    Divider,
    FormControl,
    Select,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { PlayingCard } from '../components/PlayingCard';
import { hydrateCard, hydrateInstances } from '../utilities';
import { getAuth } from 'firebase/auth';
import CardInfoDialog from '../components/CardInfoDialog';

// ---------------- Theme Tokens (Beasts of Legend) ----------------
const COL = {
    bgStart: '#000000',
    bgEnd: '#0a0a0a',
    gold: '#d4af37',
    goldSoft: 'rgba(212,175,55,0.75)',
    goldDim: 'rgba(212,175,55,0.6)',
    text: '#f9f6e3',
    panel: 'rgba(0,0,0,0.70)',
};

const fonts = { title: `'Cinzel', serif` };
const glow = (s = 0.3) => `0 0 24px rgba(212,175,55,${s}), 0 0 4px rgba(212,175,55,${s})`;

const PageWrap = styled(Box)(({ theme }) => ({
    minHeight: '100%',
    background: `linear-gradient(180deg, ${COL.bgStart}, ${COL.bgEnd})`,
    color: COL.text,
    padding: theme.spacing(3),
}));

const Section = styled(Box)(({ theme }) => ({
    background: COL.panel,
    border: `1px solid ${COL.goldDim}`,
    borderRadius: 12,
    boxShadow: `0 8px 24px rgba(212,175,55,0.15)`,
    padding: theme.spacing(2.5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontFamily: fonts.title,
    letterSpacing: '0.8px',
    color: COL.text,
    textShadow: glow(0.25),
}));

const GoldButton = styled(Button)(({ theme }) => ({
    fontFamily: fonts.title,
    letterSpacing: '0.6px',
    fontWeight: 600,
    color: COL.bgEnd,
    background: `linear-gradient(180deg, ${COL.goldSoft}, ${COL.gold})`,
    border: `1px solid ${COL.gold}`,
    boxShadow: glow(0.25),
    textTransform: 'none',
    '&:hover': {
        boxShadow: glow(0.4),
        filter: 'brightness(1.05)',
        background: `linear-gradient(180deg, ${COL.gold}, ${COL.gold})`,
    },
    '&:disabled': {
        opacity: 0.5,
        color: '#3b3b3b',
        background: 'linear-gradient(180deg, #5f5630, #4b421d)',
    },
}));

const GhostButton = styled(Button)(({ theme }) => ({
    fontFamily: fonts.title,
    color: COL.text,
    border: `1px solid ${COL.goldDim}`,
    textTransform: 'none',
    background: 'transparent',
    '&:hover': {
        background: 'rgba(212,175,55,0.08)',
        boxShadow: glow(0.25),
    },
}));

const GoldDivider = styled(Divider)({
    borderColor: COL.goldDim,
    opacity: 0.6,
});

const GoldTextField = styled(TextField)({
    '& label': { color: COL.goldDim, fontFamily: fonts.title },
    '& label.Mui-focused': { color: COL.gold },
    '& .MuiInputBase-input': { color: COL.text },
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: COL.goldDim },
        '&:hover fieldset': { borderColor: COL.gold },
        '&.Mui-focused fieldset': { borderColor: COL.gold, boxShadow: glow(0.3) },
    },
});

const Tag = styled(Box)(({ theme }) => ({
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 10,
    border: `1px solid ${COL.goldDim}`,
    fontFamily: fonts.title,
    color: COL.text,
    background: 'rgba(212,175,55,0.08)',
    boxShadow: glow(0.15),
}));

// Filter bar styling
const FilterBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: 'rgba(0,0,0,0.7)',
    borderRadius: 10,
    border: `1px solid ${COL.goldDim}`,
    boxShadow: glow(0.18),
}));

const SmallLabel = styled(Typography)(({ theme }) => ({
    fontFamily: fonts.title,
    fontSize: '0.75rem',
    letterSpacing: '0.6px',
    color: COL.goldDim,
}));

// ---------------- Logic ----------------
const CONSUMABLES = { soulDust: 'Soul Dust' };
const PAGE_SIZE = 25;

const api = {
    async call(name, body) {
        const idToken = await getAuth().currentUser?.getIdToken();
        const url = `https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/${name}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify(body || {}),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.error) throw new Error(json?.error || 'error');
        return json;
    },
    createListing(payload) { return api.call('createBazaarListing', payload); },
    buyListing(listingId) { return api.call('buyBazaarListing', { listingId }); },
    cancelListing(listingId) { return api.call('cancelBazaarListing', { listingId }); },
    listListings(payload) { return api.call('listBazaarListings', payload); },
};

export default function Bazzar() {
    const { userData, setUserData, globalCards } = useContext(UserContext);
    const userId = userData?.uid;

    const [myListings, setMyListings] = useState([]);

    // Public listings: server-side pages
    const [pages, setPages] = useState([]); // each { listings, nextCursor }
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [loadingListings, setLoadingListings] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showSnackbar = (message) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    // Raw instances and hydrated versions for UI
    const ownedInstances = userData?.ownedCards || [];
    const ownedHydrated = useMemo(
        () => hydrateInstances(ownedInstances, globalCards),
        [ownedInstances, globalCards]
    );
    const soulDust = Number(userData?.consumables?.soulDust || 0);

    // ⭐ Listing-by-click state (CardInfoDialog)
    const [listingDialogOpen, setListingDialogOpen] = useState(false);
    const [listingInstance, setListingInstance] = useState(null);
    const [listingCardHydrated, setListingCardHydrated] = useState(null);
    const [listingPriceInput, setListingPriceInput] = useState('');

    const openListDialogForCard = (hydratedCard, instance) => {
        if (!hydratedCard || !instance) return;
        setListingInstance(instance);
        setListingCardHydrated(hydratedCard);
        setListingPriceInput('');
        setListingDialogOpen(true);
    };

    const closeListDialog = () => {
        setListingDialogOpen(false);
        setListingInstance(null);
        setListingCardHydrated(null);
        setListingPriceInput('');
    };

    const parsedListingPrice = useMemo(() => {
        const parsed = parseInt(String(listingPriceInput || ''), 10);
        return Number.isFinite(parsed) ? parsed : 0;
    }, [listingPriceInput]);

    const listingPriceValid = parsedListingPrice >= 1;

    // ⭐ NEW: inspected card for viewing (market + your listings)
    const [inspectedCard, setInspectedCard] = useState(null);

    // Filters
    const [rarityFilter, setRarityFilter] = useState('all');
    const [classificationFilter, setClassificationFilter] = useState('all');
    const [statField, setStatField] = useState('none'); // 'none' | 'attack' | 'defense'
    const [statComparison, setStatComparison] = useState('above'); // 'above' | 'below'
    const [statValue, setStatValue] = useState('');
    const [priceComparison, setPriceComparison] = useState('any'); // 'any' | 'above' | 'below'
    const [priceValue, setPriceValue] = useState('');

    // Fetch MY Bazaar Listings via Firestore query (sellerId == userId)
    useEffect(() => {
        if (!userId) return;
        (async () => {
            try {
                const qMy = query(collection(db, 'bazzarListings'), where('sellerId', '==', userId));
                const snapshot = await getDocs(qMy);
                const my = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setMyListings(my);
            } catch (error) {
                console.error('Error fetching my bazzarListings:', error);
                setMyListings([]);
            }
        })();
    }, [userId]);

    // --- Server-side listing helpers ---
    const buildListPayload = (cursor) => ({
        pageSize: PAGE_SIZE,
        cursor: cursor || null,
        rarityFilter,
        classificationFilter,
        statField,
        statComparison,
        statValue: statValue === '' ? null : Number(statValue),
        priceComparison,
        priceValue: priceValue === '' ? null : Number(priceValue),
    });

    async function loadFirstPage() {
        if (!userId) return;
        setLoadingListings(true);
        try {
            const body = buildListPayload(null);
            const { listings = [], nextCursor = null } = await api.listListings(body);
            const filtered = listings.filter(l => l.sellerId !== userId);
            setPages([{ listings: filtered, nextCursor }]);
            setCurrentPageIndex(0);
        } catch (e) {
            console.error('loadFirstPage error', e);
            showSnackbar(e.message || 'Failed to load listings');
            setPages([]);
            setCurrentPageIndex(0);
        } finally {
            setLoadingListings(false);
        }
    }

    async function loadNextPage() {
        const currentPage = pages[currentPageIndex];
        if (!currentPage || !currentPage.nextCursor) return;

        setLoadingListings(true);
        try {
            const body = buildListPayload(currentPage.nextCursor);
            const { listings = [], nextCursor = null } = await api.listListings(body);
            const filtered = listings.filter(l => l.sellerId !== userId);

            setPages(prev => [...prev, { listings: filtered, nextCursor }]);
            setCurrentPageIndex(prev => prev + 1);
        } catch (e) {
            console.error('loadNextPage error', e);
            showSnackbar(e.message || 'Failed to load next page');
        } finally {
            setLoadingListings(false);
        }
    }

    useEffect(() => {
        if (!userId) return;
        loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, rarityFilter, classificationFilter, statField, statComparison, statValue, priceComparison, priceValue]);

    // --- Listing actions ---
    const handleConfirmListForSale = async () => {
        if (!listingInstance?.instanceId) return;

        const parsedAmount = parseInt(String(listingPriceInput || ''), 10);
        if (!Number.isFinite(parsedAmount) || parsedAmount < 1) {
            showSnackbar('Enter a valid Soul Dust amount');
            return;
        }

        try {
            const { listingId } = await api.createListing({
                instanceId: listingInstance.instanceId,
                price: parsedAmount,
            });

            // Optimistic: remove from ownedCards locally
            const newCards = (userData.ownedCards || []).filter(c => c.instanceId !== listingInstance.instanceId);
            setUserData(prev => ({ ...prev, ownedCards: newCards }));

            setMyListings(prev => [
                ...prev,
                {
                    id: listingId,
                    sellerId: userId,
                    card: { ...listingInstance },
                    cost: { itemId: 'soulDust', amount: parsedAmount },
                    price: parsedAmount,
                },
            ]);

            closeListDialog();
            showSnackbar('Listed!');
            loadFirstPage();
        } catch (e) {
            showSnackbar(e.message || 'Failed to list');
        }
    };

    const handleCancelListing = async (listing) => {
        try {
            await api.cancelListing(listing.id);
            setMyListings(prev => prev.filter(l => l.id !== listing.id));
            showSnackbar('Listing canceled');
            loadFirstPage();
        } catch (e) {
            showSnackbar(e.message || 'Cancel failed');
        }
    };

    const handleBuy = async (listing) => {
        try {
            const price = Number(listing.cost?.amount ?? listing.price ?? 0);
            if (soulDust < price) {
                showSnackbar('Not enough Soul Dust.');
                return;
            }

            await api.buyListing(listing.id);

            setUserData(prev => ({
                ...prev,
                consumables: {
                    ...(prev.consumables || {}),
                    soulDust: Number(prev.consumables?.soulDust || 0) - price,
                },
            }));

            setPages(prev => {
                const next = [...prev];
                const page = next[currentPageIndex];
                if (!page) return prev;
                page.listings = page.listings.filter(l => l.id !== listing.id);
                next[currentPageIndex] = page;
                return next;
            });

            showSnackbar('Purchase successful!');
        } catch (e) {
            showSnackbar(e.message || 'Purchase failed');
        }
    };

    const handleInspectListing = (listing) => {
        if (!listing?.card) return;
        const hydrated = hydrateCard(listing.card, globalCards) || listing.card;
        setInspectedCard(hydrated);
    };

    // Range filter mutual exclusion
    const handleStatFieldChange = (value) => {
        setStatField(value);
        if (value !== 'none') {
            setPriceComparison('any');
            setPriceValue('');
        }
    };

    const handlePriceComparisonChange = (value) => {
        setPriceComparison(value);
        if (value !== 'any') {
            setStatField('none');
            setStatValue('');
        }
    };

    const currentPage = pages[currentPageIndex] || { listings: [], nextCursor: null };
    const pageListings = currentPage.listings || [];

    // ⭐ Footer UI for listing dialog: input + button only
    const listFooter = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <GoldTextField
                label="List Price (Soul Dust)"
                value={listingPriceInput}
                onChange={(e) => setListingPriceInput(e.target.value)}
                type="number"
                fullWidth
            />
            <GoldButton
                onClick={handleConfirmListForSale}
                disabled={!listingInstance || !listingPriceValid}
                sx={{ alignSelf: 'flex-end' }}
            >
                List for Sale
            </GoldButton>
        </Box>
    );

    return (
        <PageWrap>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Tag>Balance</Tag>
                <Typography sx={{ fontFamily: fonts.title, letterSpacing: '0.6px' }}>
                    Your Soul Dust:&nbsp;
                    <Box component="span" sx={{ color: COL.gold, textShadow: glow(0.5) }}>{soulDust}</Box>
                </Typography>
            </Box>

            {/* ⭐ Listing Dialog: CardInfoDialog w/ ONLY input + list button */}
            <CardInfoDialog
                open={listingDialogOpen}
                onClose={closeListDialog}
                card={listingCardHydrated}
                readOnly
                hideDefaultActions
                hideCloseButton
                footerContent={listFooter}
            />

            {/* ⭐ Inspect dialog for market cards / your listings (normal readOnly view) */}
            <CardInfoDialog
                open={!!inspectedCard}
                onClose={() => setInspectedCard(null)}
                card={inspectedCard}
                readOnly
            />

            {/* List Card Section */}
            <Section sx={{ mb: 4 }}>
                <SectionTitle variant="h5" gutterBottom>List a Card for Sale</SectionTitle>
                <GoldDivider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {ownedHydrated.map((card) => {
                        const instance = ownedInstances.find(i => i.instanceId === card.instanceId);
                        const ascensions = instance?.ascensions ?? card.ascensions ?? 0;

                        return (
                            <Grid item key={card.instanceId || `${card.cardId}-${card.level}-${card.evolutionStage}`}>
                                <Box
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            transition: '150ms',
                                            filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))',
                                        }
                                    }}
                                    onClick={() => {
                                        const chosenInstance = ownedInstances.find(i => i.instanceId === card.instanceId);
                                        if (!chosenInstance) return;
                                        openListDialogForCard(card, chosenInstance);
                                    }}
                                >
                                    <PlayingCard card={card} size="sm" />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            textAlign: 'center',
                                            mt: 0.5,
                                            fontFamily: fonts.title
                                        }}
                                    >
                                        Lv {card.level} • Evo {card.evolutionStage} • Asc {ascensions}
                                    </Typography>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Section>

            {/* Your Listings */}
            <Section sx={{ mb: 4 }}>
                <SectionTitle variant="h5" gutterBottom>Your Listings</SectionTitle>
                <GoldDivider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {myListings.map(listing => {
                        const displayCard = hydrateCard(listing.card, globalCards) || listing.card;
                        const ascensions = displayCard.ascensions ?? listing.card?.ascensions ?? 0;

                        return (
                            <Grid item key={listing.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                transition: '150ms',
                                                filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))',
                                            }
                                        }}
                                        onClick={() => handleInspectListing(listing)}
                                    >
                                        <PlayingCard card={displayCard} size="sm" showStats showLevel />
                                    </Box>

                                    <Typography variant="caption" sx={{ mt: 0.5, fontFamily: fonts.title, textAlign: 'center' }}>
                                        Lv {displayCard.level} • Evo {displayCard.evolutionStage} • Asc {ascensions}
                                    </Typography>

                                    <Typography variant="body2" sx={{ mt: 1, fontFamily: fonts.title }}>
                                        Listed for:{' '}
                                        <Box component="span" sx={{ color: COL.gold }}>
                                            {listing.price ?? listing.cost?.amount}
                                        </Box>{' '}
                                        {CONSUMABLES[listing.cost?.itemId] || 'Soul Dust'}
                                    </Typography>

                                    <GhostButton sx={{ mt: 1 }} onClick={() => handleCancelListing(listing)}>
                                        Cancel Listing
                                    </GhostButton>
                                </Box>
                            </Grid>
                        );
                    })}

                    {myListings.length === 0 && (
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: fonts.title,
                                    color: COL.goldDim,
                                    textAlign: 'center',
                                    mt: 1,
                                }}
                            >
                                You have no active listings.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Section>

            {/* Market Listings */}
            <Section>
                <SectionTitle variant="h5" gutterBottom>Available Cards</SectionTitle>
                <GoldDivider sx={{ mb: 2 }} />

                {/* Filter Bar */}
                <FilterBar>
                    <Box sx={{ minWidth: 140 }}>
                        <SmallLabel>Rarity</SmallLabel>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={rarityFilter}
                                onChange={(e) => setRarityFilter(e.target.value)}
                                displayEmpty
                                sx={{
                                    mt: 0.5,
                                    color: COL.text,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: COL.goldDim },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COL.gold },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COL.gold },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            background: '#0b0b0b',
                                            color: COL.text,
                                            border: `1px solid ${COL.goldDim}`,
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="Common">Common</MenuItem>
                                <MenuItem value="Uncommon">Uncommon</MenuItem>
                                <MenuItem value="Rare">Rare</MenuItem>
                                <MenuItem value="High Rare">High Rare</MenuItem>
                                <MenuItem value="Super Rare">Super Rare</MenuItem>
                                <MenuItem value="Super Super Rare">Super Super Rare</MenuItem>
                                <MenuItem value="Legendary">Legendary</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ minWidth: 140 }}>
                        <SmallLabel>Classification</SmallLabel>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={classificationFilter}
                                onChange={(e) => setClassificationFilter(e.target.value)}
                                displayEmpty
                                sx={{
                                    mt: 0.5,
                                    color: COL.text,
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: COL.goldDim },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COL.gold },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COL.gold },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            background: '#0b0b0b',
                                            color: COL.text,
                                            border: `1px solid ${COL.goldDim}`,
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="Man">Man</MenuItem>
                                <MenuItem value="Beast">Beast</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                        <SmallLabel>Stat Filter</SmallLabel>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <FormControl size="small" sx={{ minWidth: 110 }}>
                                <Select
                                    value={statField}
                                    onChange={(e) => handleStatFieldChange(e.target.value)}
                                    sx={{
                                        color: COL.text,
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: COL.goldDim },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COL.gold },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COL.gold },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                background: '#0b0b0b',
                                                color: COL.text,
                                                border: `1px solid ${COL.goldDim}`,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="attack">ATK</MenuItem>
                                    <MenuItem value="defense">DEF</MenuItem>
                                </Select>
                            </FormControl>

                            <ToggleButtonGroup
                                size="small"
                                value={statComparison}
                                exclusive
                                onChange={(_, v) => v && setStatComparison(v)}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${COL.goldDim}`,
                                    '& .MuiToggleButton-root': {
                                        color: COL.text,
                                        borderColor: COL.goldDim,
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(212,175,55,0.12)',
                                            color: COL.gold,
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(212,175,55,0.08)',
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="above">≥</ToggleButton>
                                <ToggleButton value="below">≤</ToggleButton>
                            </ToggleButtonGroup>

                            <GoldTextField
                                value={statValue}
                                onChange={(e) => setStatValue(e.target.value)}
                                placeholder="Value"
                                type="number"
                                size="small"
                                sx={{ maxWidth: 100 }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                        <SmallLabel>Price Filter (Soul Dust)</SmallLabel>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <ToggleButtonGroup
                                size="small"
                                value={priceComparison}
                                exclusive
                                onChange={(_, v) => v && handlePriceComparisonChange(v)}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${COL.goldDim}`,
                                    '& .MuiToggleButton-root': {
                                        color: COL.text,
                                        borderColor: COL.goldDim,
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(212,175,55,0.12)',
                                            color: COL.gold,
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(212,175,55,0.08)',
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="any">Any</ToggleButton>
                                <ToggleButton value="above">≥</ToggleButton>
                                <ToggleButton value="below">≤</ToggleButton>
                            </ToggleButtonGroup>

                            <GoldTextField
                                value={priceValue}
                                onChange={(e) => setPriceValue(e.target.value)}
                                placeholder="Dust"
                                type="number"
                                size="small"
                                sx={{ maxWidth: 120 }}
                            />
                        </Box>
                    </Box>
                </FilterBar>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography sx={{ fontFamily: fonts.title, fontSize: '0.85rem', color: COL.text }}>
                        Page {currentPageIndex + 1} {loadingListings && '— Loading...'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <GhostButton
                            size="small"
                            disabled={currentPageIndex === 0 || loadingListings}
                            onClick={() => setCurrentPageIndex(i => Math.max(0, i - 1))}
                        >
                            Previous
                        </GhostButton>
                        <GoldButton
                            size="small"
                            disabled={loadingListings || !currentPage.nextCursor}
                            onClick={loadNextPage}
                        >
                            Next
                        </GoldButton>
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    {pageListings.map(listing => {
                        const displayCard = hydrateCard(listing.card, globalCards) || listing.card;
                        const disabled = soulDust < Number(listing.cost?.amount ?? listing.price ?? 0);
                        const ascensions = displayCard.ascensions ?? listing.card?.ascensions ?? 0;

                        return (
                            <Grid item key={listing.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                transition: '150ms',
                                                filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))',
                                            }
                                        }}
                                        onClick={() => handleInspectListing(listing)}
                                    >
                                        <PlayingCard card={displayCard} size="sm" showStats showLevel />
                                    </Box>

                                    <Typography variant="caption" sx={{ mt: 0.5, fontFamily: fonts.title, textAlign: 'center' }}>
                                        Lv {displayCard.level} • Evo {displayCard.evolutionStage} • Asc {ascensions}
                                    </Typography>

                                    <Typography variant="caption" sx={{ fontFamily: fonts.title, color: COL.goldDim, mt: 0.25 }}>
                                        ATK {listing.attack ?? '?'} • DEF {listing.defense ?? '?'}
                                    </Typography>

                                    <Typography variant="body2" sx={{ mt: 1, fontFamily: fonts.title }}>
                                        Price:{' '}
                                        <Box component="span" sx={{ color: COL.gold }}>
                                            {listing.price ?? listing.cost?.amount}
                                        </Box>{' '}
                                        {CONSUMABLES[listing.cost?.itemId] || 'Soul Dust'}
                                    </Typography>

                                    <GoldButton onClick={() => handleBuy(listing)} disabled={disabled} sx={{ mt: 1 }}>
                                        Buy
                                    </GoldButton>
                                </Box>
                            </Grid>
                        );
                    })}

                    {pageListings.length === 0 && !loadingListings && (
                        <Grid item xs={12}>
                            <Typography sx={{ fontFamily: fonts.title, color: COL.goldDim, textAlign: 'center', mt: 2 }}>
                                No listings match your search.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Section>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{
                    sx: {
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        px: 4,
                        py: 1.5,
                        backgroundColor: COL.panel,
                        color: COL.text,
                        borderRadius: 2,
                        border: `1px solid ${COL.goldDim}`,
                        boxShadow: glow(0.25),
                        fontFamily: fonts.title,
                    }
                }}
                message={snackbarMessage}
            />
        </PageWrap>
    );
}
